import { useQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import { useCallback, useMemo } from 'react';
import type { FieldArrayWithId } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ListCountriesDocument,
  type ListCountriesQuery,
} from '../../../../../../../../graphql/fragment/audiences/ListCountries.generated.tsx';
import {
  ListRegionsDocument,
  type ListRegionsQuery,
} from '../../../../../../../../graphql/fragment/audiences/ListRegions.generated.tsx';
import type {
  AudienceForm,
  AudienceListType,
  FormSegment,
  Group,
  GroupFieldName,
} from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import { sortSegmentsByExpiration } from '../../../../../utils/sortSegmentsByExpiration.ts';
import { ConfirmationModal } from '../../../../ConfirmationModal.tsx';
import type { DrawerState } from '../../../drawerTypes.ts';
import { ExpandableSectionWithAccordion } from '../ExpandableSectionWithAccordion.tsx';
import { SegmentListItem } from './SegmentListItem.tsx';
import { useSegmentsSectionHandlers } from './useSegmentsSectionHandlers.ts';

export type SegmentsSectionProps = {
  field: FieldArrayWithId<
    AudienceForm,
    'normalized_definition.includes.groups' | 'normalized_definition.excludes.groups',
    'id'
  > &
    Group;
  isEmptyGroup: boolean;
  groupName: string;
  groupFieldName: GroupFieldName;
  listType: AudienceListType;
  setDrawerState: (state: DrawerState) => void;
  update: (index: number, value: Group) => void;
  index: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isEdit?: boolean;
};

export const SegmentsSection = ({
  field,
  groupName,
  groupFieldName,
  listType,
  setDrawerState,
  update,
  index,
  isExpanded,
  isEdit = false,
  onToggleExpand,
}: SegmentsSectionProps) => {
  const { t } = useTranslation();
  const fieldAsGroup: Group = field;

  const allSegments = useMemo(() => {
    const segments: FormSegment[] = [];

    if (fieldAsGroup.direct?.segments?.length) {
      segments.push(...fieldAsGroup.direct.segments);
    }

    if (fieldAsGroup.predefined?.segments?.length) {
      segments.push(...fieldAsGroup.predefined.segments);
    }

    return sortSegmentsByExpiration(segments);
  }, [fieldAsGroup]);

  const segmentCount = allSegments?.length ?? 0;

  const onExpandSection = useCallback(() => {
    if (!isExpanded) onToggleExpand();
  }, [isExpanded, onToggleExpand]);

  const onShrinkSection = useCallback(() => {
    if (isExpanded) onToggleExpand();
  }, [isExpanded, onToggleExpand]);

  const {
    showClearModal,
    handleEditClick,
    handleRemoveSegment,
    handleClearButtonClick,
    handleClearConfirm,
    handleClearCancel,
  } = useSegmentsSectionHandlers({
    fieldAsGroup,
    groupName,
    groupFieldName,
    listType,
    index,
    update,
    setDrawerState,
    onShrinkSection,
    onExpandSection,
  });

  const segmentsTitle = segmentCount > 0 ? t('Segments [ {count} ]', { count: segmentCount }) : t('Segments');

  const { data: regionsData } = useQuery<ListRegionsQuery>(ListRegionsDocument);
  const { data: countriesData } = useQuery<ListCountriesQuery>(ListCountriesDocument);
  const regions = regionsData?.region ?? [];
  const countries = countriesData?.country ?? [];

  const shouldShowSubtitle = segmentCount === 0;
  const editProps = isEdit
    ? {
        showEditButton: true as const,
        showClearButton: segmentCount > 0,
        disableEditButton: false,
        onEditClick: handleEditClick,
        onClear: handleClearButtonClick,
      }
    : {};

  return (
    <>
      <ExpandableSectionWithAccordion
        title={segmentsTitle}
        dataTest="segments-section"
        subtitle={shouldShowSubtitle ? t('Segments not applied') : undefined}
        {...editProps}
        expandable={segmentCount > 0}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        expandableSectionStyles={{
          accordionDetails: {
            px: 0,
            maxHeight: '400px',
            overflow: 'auto',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {allSegments.map(segment => (
            <SegmentListItem
              key={segment.id}
              segment={segment}
              isEdit={isEdit}
              onDelete={() => handleRemoveSegment(segment)}
              regions={regions}
              countries={countries}
            />
          ))}
        </Box>
      </ExpandableSectionWithAccordion>

      <ConfirmationModal
        isOpen={showClearModal}
        onClose={handleClearCancel}
        onDelete={handleClearConfirm}
        title={t('Clear all segments')}
        item={t('All segments')}
      />
    </>
  );
};
